import React, { useState } from 'react';
import { Calendar, Clock, User, Mail, Phone, MessageSquare } from 'lucide-react';
import { Seo } from '../components/Seo';
import { BUSINESS } from '../data/site';
type Status = 'idle' | 'submitting' | 'success' | 'error';
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xqejbozd';
const INPUT_CLASS = 'w-full px-4 py-3 bg-white border border-gray-400 rounded focus:outline-none focus:border-reach-red focus:ring-1 focus:ring-reach-red transition-colors font-inter';
export function Book() {
  const [status, setStatus] = useState<Status>('idle');
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data: Record<string, string> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });
    setStatus('submitting');
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        form.reset();
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };
  const resetForm = () => setStatus('idle');
  return <div className="w-full bg-white min-h-screen" data-id="element-265" data-mp="tv930q53nx5a">
      <Seo title="Book a Free Athlete Evaluation | Reach Performance McKinney" description="Request a free athlete evaluation with Coach Fernando Beltran at INPOWERiQ in McKinney, TX. Tell us about your athlete and we will recommend the right group." path="/book" data-id="element-266" data-mp="17rcuibol9re2" />
      <section className="py-12 md:py-20" data-id="element-267" data-mp="1ckffhvfavsin">
        <div className="max-w-container mx-auto px-4 md:px-8" data-id="element-268" data-mp="1clqiht0x8w8p">
          <div className="flex flex-col md:flex-row gap-12 lg:gap-20" data-id="element-269" data-mp="1qjx9cp7gdvyv">
            <div className="flex-1" data-id="element-270" data-mp="1lku1kx039bn7">
              <h1 className="font-bebas text-5xl md:text-7xl mb-6 tracking-wide" data-id="element-271" data-mp="1lln9i1asa76x">
                Book Your Free
                <br data-id="element-272" data-mp="j4js6os10mnw" />
                <span className="text-reach-red" data-id="element-273" data-mp="o5r0qq1ztpvm">Athlete Evaluation</span>
              </h1>
              <p className="font-inter text-lg text-reach-black mb-6" data-id="element-274" data-mp="s7cjgs17kydq">
                Take the first step. Send the form and we will follow up to schedule a facility tour
                and evaluation.
              </p>

              <div className="mb-10 flex flex-col sm:flex-row gap-3" data-id="element-275" data-mp="11xnoyj0jjfmq">
                <a href={BUSINESS.smsHref} className="btn-secondary" data-id="element-276" data-mp="11zi7jqocavnn">
                  Text {BUSINESS.phoneDisplay}
                </a>
                <a href={BUSINESS.phoneHref} className="btn-secondary" data-id="element-277" data-mp="1bv25eimgs4f1">
                  Call {BUSINESS.phoneDisplay}
                </a>
              </div>

              <div className="space-y-6" data-id="element-278" data-mp="1fukmt4f2txd4">
                <div className="flex items-start gap-4" data-id="element-279" data-mp="3dngfwwmoi6lf">
                  <div className="w-12 h-12 bg-reach-gray rounded-full flex items-center justify-center shrink-0" data-id="element-280" data-mp="3dp1ecxc469ok">
                    <Calendar className="text-reach-red" size={24} aria-hidden="true" data-id="element-281" data-mp="3a4hfmox9hefg" />
                  </div>
                  <div data-id="element-282" data-mp="35div1d58pu6e">
                    <h2 className="font-bebas text-2xl tracking-wide mb-1" data-id="element-283" data-mp="35d951pk73n0p">What to expect</h2>
                    <p className="font-inter text-sm text-gray-700" data-id="element-284" data-mp="30fzhkfgd9lfi">
                      A walkthrough of {BUSINESS.venue}, a movement screen, and a conversation about
                      your athlete's goals.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4" data-id="element-285" data-mp="3o4acz33amswe">
                  <div className="w-12 h-12 bg-reach-gray rounded-full flex items-center justify-center shrink-0" data-id="element-286" data-mp="3o2pifdgzb9yj">
                    <Clock className="text-reach-red" size={24} aria-hidden="true" data-id="element-287" data-mp="3f3ubu0a0vxup" />
                  </div>
                  <div data-id="element-288" data-mp="28nicvwzdmby2">
                    <h2 className="font-bebas text-2xl tracking-wide mb-1" data-id="element-289" data-mp="28otd5mtm51kf">Flexible scheduling</h2>
                    <p className="font-inter text-sm text-gray-700" data-id="element-290" data-mp="23nnivkkb168s">
                      We contact you within 24 hours to find a time that works for your family's
                      schedule.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1" data-id="element-291" data-mp="3ilq4jgh6osrq">
              <div className="bg-reach-gray p-8 rounded-lg border-t-4 border-reach-red" data-id="element-292" data-mp="3iilva4ov36u7">
                {status === 'success' ? <div className="text-center py-12" data-id="element-293" data-mp="e5ys1hg28mt4">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-294" data-mp="e79t39i4fggj">
                      <svg className="w-8 h-8 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" data-id="element-295" data-mp="j3791eph2kss">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-296" data-mp="2uvdxs8xod22q" />
                      </svg>
                    </div>
                    <h2 className="font-bebas text-3xl tracking-wide mb-4" data-id="element-297" data-mp="2wcjertgzp8o1">
                      Thanks, your message is in
                    </h2>
                    <p className="font-inter text-reach-black mb-6" data-id="element-298" data-mp="2rb3rrvv94s30">
                      Coach Fernando typically responds within 24 hours.
                    </p>
                    <button type="button" onClick={resetForm} className="inline-flex items-center justify-center font-inter font-semibold text-sm uppercase tracking-wider px-5 py-2.5 border-2 border-reach-black text-reach-black rounded hover:bg-reach-black hover:text-white transition-colors" data-id="element-299" data-mp="1ypkhbgih3wp3">
                      Send another
                    </button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-300" data-mp="3fz7x0oa5ggpk">
                    <h2 className="sr-only" data-id="element-301" data-mp="3kz41eomrea5n">Evaluation request form</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-302" data-mp="3kx9ej4rxg7yx">
                      <div data-id="element-303" data-mp="3pqm1lagyk6pk">
                        <label htmlFor="parentName" className="block font-inter text-sm font-semibold mb-2" data-id="element-304" data-mp="3psgeq2lzmuj3">
                          Parent or guardian name
                        </label>
                        <div className="relative" data-id="element-305" data-mp="3211hupczd5f8">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-306" data-mp="362cyw6hsgibt">
                            <User size={18} className="text-gray-500" aria-hidden="true" data-id="element-307" data-mp="3b1g6nyp5l2nh" />
                          </div>
                          <input type="text" id="parentName" name="parentName" required autoComplete="name" className={`${INPUT_CLASS} pl-10`} placeholder="John Doe" data-id="element-308" data-mp="lb2smcq2htph" />
                        </div>
                      </div>
                      <div data-id="element-309" data-mp="mnh8f8xe3wjj">
                        <label htmlFor="athleteName" className="block font-inter text-sm font-semibold mb-2" data-id="element-310" data-mp="moaiairnb25w">
                          Athlete name
                        </label>
                        <div className="relative" data-id="element-311" data-mp="crnyzyeywj5z">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-312" data-mp="7r9pebxpx15u">
                            <User size={18} className="text-gray-500" aria-hidden="true" data-id="element-313" data-mp="3py58lxm5851" />
                          </div>
                          <input type="text" id="athleteName" name="athleteName" required autoComplete="off" className={`${INPUT_CLASS} pl-10`} placeholder="Jane Doe" data-id="element-314" data-mp="22258w9afy0ne" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-315" data-mp="2cjeaa0uqg3sx">
                      <div data-id="element-316" data-mp="1xn5dd5ybe2jo">
                        <label htmlFor="email" className="block font-inter text-sm font-semibold mb-2" data-id="element-317" data-mp="1xlvdqcbtcmax">
                          Email address
                        </label>
                        <div className="relative" data-id="element-318" data-mp="1tljrww7psfxv">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-319" data-mp="1tm3cylsnvkg4">
                            <Mail size={18} className="text-gray-500" aria-hidden="true" data-id="element-320" data-mp="3iyonn5qqlktl" />
                          </div>
                          <input type="email" id="email" name="email" required autoComplete="email" className={`${INPUT_CLASS} pl-10`} placeholder="john@example.com" data-id="element-321" data-mp="1yeunly1t80r" />
                        </div>
                      </div>
                      <div data-id="element-322" data-mp="knlcz7uxjxm1">
                        <label htmlFor="phone" className="block font-inter text-sm font-semibold mb-2" data-id="element-323" data-mp="kqzfqh7ia8u0">
                          Phone number
                        </label>
                        <div className="relative" data-id="element-324" data-mp="pnptgxvu8sc9">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-325" data-mp="pm519ewzxlhk">
                            <Phone size={18} className="text-gray-500" aria-hidden="true" data-id="element-326" data-mp="2rfchcqkfo74i" />
                          </div>
                          <input type="tel" id="phone" name="phone" required autoComplete="tel" className={`${INPUT_CLASS} pl-10`} placeholder="(555) 123-4567" data-id="element-327" data-mp="2pfnz3uubc0lm" />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-328" data-mp="3957t3z5qr49l">
                      <div data-id="element-329" data-mp="3453lkymt9jvi">
                        <label htmlFor="program" className="block font-inter text-sm font-semibold mb-2" data-id="element-330" data-mp="2zdlk89gvlha0">
                          Program of interest
                        </label>
                        <select id="program" name="program" required autoComplete="off" className={INPUT_CLASS} data-id="element-331" data-mp="3rvh4nznf48dy">
                          <option value="" data-id="element-332" data-mp="37839s57nx2d">Select a program</option>
                          <option value="advanced-performance" data-id="element-333" data-mp="87463o269igc">
                            Advanced Performance, high school, Tue and Thu 5:45 PM
                          </option>
                          <option value="homeschool" data-id="element-334" data-mp="d44ei5mhuxam">
                            Homeschool Multisport, daytime blocks
                          </option>
                          <option value="speed-school" data-id="element-335" data-mp="m5u3l4swlk58">
                            Speed School, September 12 and 26
                          </option>
                          <option value="private" data-id="element-336" data-mp="qzyd9vtk06a0">Private or semi-private training</option>
                          <option value="college-pro" data-id="element-337" data-mp="vyjcbrx2kjwv">College or pro training</option>
                          <option value="not-sure" data-id="element-338" data-mp="10xmff6thog27">Not sure yet</option>
                        </select>
                      </div>
                      <div data-id="element-339" data-mp="31xrf940x8b6i">
                        <label htmlFor="sport" className="block font-inter text-sm font-semibold mb-2" data-id="element-340" data-mp="31z2in2qgfgx3">
                          Primary sport
                        </label>
                        <input type="text" id="sport" name="sport" autoComplete="off" className={INPUT_CLASS} placeholder="Football, soccer, basketball" data-id="element-341" data-mp="3as5tr4dmnhok" />
                      </div>
                    </div>

                    <div data-id="element-342" data-mp="3tn2weboxkquc">
                      <label htmlFor="goals" className="block font-inter text-sm font-semibold mb-2" data-id="element-343" data-mp="3pnv5m9p3mefk">
                        Goals or notes
                      </label>
                      <div className="relative" data-id="element-344" data-mp="1zd8pin8vglwm">
                        <div className="absolute top-3 left-3 pointer-events-none" data-id="element-345" data-mp="1vacf3jthjw3r">
                          <MessageSquare size={18} className="text-gray-500" aria-hidden="true" data-id="element-346" data-mp="1l5423b6rg97k" />
                        </div>
                        <textarea id="goals" name="goals" rows={4} autoComplete="off" className={`${INPUT_CLASS} pl-10 resize-none`} placeholder="What are you looking to improve?" data-id="element-347" data-mp="1p666s7jg5dgx" />
                      </div>
                    </div>

                    <button type="submit" disabled={status === 'submitting'} className="btn-primary w-full disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-348" data-mp="16iavcubb4bqz">
                      {status === 'submitting' ? 'Sending' : 'Request Free Evaluation'}
                    </button>

                    <p aria-live="polite" data-id="element-349" data-mp="2rum2s9cz5zta">
                      {status === 'error' && <span className="block font-inter text-sm text-red-700 text-center" data-id="element-350" data-mp="rscsslq1hxac">
                          Something went wrong. Please email {BUSINESS.email} or call{' '}
                          {BUSINESS.phoneDisplay}.
                        </span>}
                    </p>
                  </form>}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}